import { useCallback, useEffect, useMemo, useState } from 'react'

import { PointCloudOctree } from '@datalabs-jp/three-loader'
import { EDITOR_TOOLS } from '@editor/config/constants'
import { useEditorContext } from '@editor/contexts/Editor'
import { PointArray } from '@editor/interfaces/attribute'
import { SolidMeshColorSampler } from '@editor/interfaces/editor'
import { sampleTextureColorAtIntersection } from '@editor/services/Color'
import { findObjectsOfShapeIds } from '@editor/services/Object3D'
import { generateCuboidUniforms, getClickCoords } from '@editor/services/Points'
import { useAppSelector } from '@editor/store/app'
import { Box3, Camera, Intersection, Matrix4, Object3D, Points, Raycaster, Vector3 } from 'three'

import { getCurrentEditorTool } from '../tools/setup'

interface MaskModelMatrix {
  modelMatrix: Matrix4
  min: Vector3
  max: Vector3
}

/**
 * What a raycast against the canvas yields: the point on the PCD/objects, the point on the
 * solid mesh, and a sampler for the solid mesh's texture colour there.
 */
export type SelectedPoint = [PointArray | undefined, PointArray | undefined, SolidMeshColorSampler | undefined]

const rayCaster = new Raycaster()

const usePointSelection = (pointClouds: PointCloudOctree[]) => {
  // Contexts
  const { sceneRef, meshRefs, cameraRef, pointSelectionPlane, pointProjectionPlane, pointSelectionObjectIdsRef } =
    useEditorContext()

  // Store
  const selectedTool = useAppSelector((state) => state.editor.selectedTool)
  const prevSelectedTool = useAppSelector((state) => state.editor.prevSelectedTool)
  const anchorPlacementObjectIds = useAppSelector((state) => state.editor.anchorPlacementObjects)
  const isPointCloudVisible = useAppSelector((state) => state.editor.isPcdVisible)
  const isMeshFileVisible = useAppSelector((state) => state.solidMesh.isVisible)

  const maskRegions = useAppSelector((state) => state.maskPCD.regions)

  // State
  const [maskModelMatrices, setMaskModelMatrices] = useState<MaskModelMatrix[]>([])

  // Derived state
  const isViewAllPoints = useMemo(
    () =>
      selectedTool === EDITOR_TOOLS.PCD_TRIM_CUBOID ||
      (EDITOR_TOOLS.FOCUS === selectedTool && prevSelectedTool === EDITOR_TOOLS.PCD_TRIM_CUBOID) ||
      maskRegions.filter((region) => region.invisible).length === maskRegions.length,
    [selectedTool, prevSelectedTool, maskRegions],
  )

  /**
   * Update mask model matrices when mask regions change
   */
  useEffect(() => {
    setMaskModelMatrices(maskRegions.filter((region) => !region.invisible).map(generateCuboidUniforms))
  }, [maskRegions])

  /**
   * Get selected point from mouse or touch event.
   *
   * Returns the point on the PCD/objects, the point on the solid mesh, and a sampler for
   * the solid mesh's texture colour at that point (the last is undefined when the ray
   * missed the solid mesh, and samples nothing until it is called).
   *
   * @param mouseEvent
   * @param touchEvent
   */
  const getSelectedPoint = useCallback(
    (
      mouseEvent?: React.MouseEvent<HTMLDivElement, MouseEvent>,
      touchEvent?: React.TouchEvent<HTMLDivElement>,
    ): SelectedPoint | undefined => {
      if (!cameraRef?.current) return undefined

      //* カメラ取得
      const camera = cameraRef.current as Camera

      //* 光線生成とクリック位置特定
      const targetElem = document.getElementById('canvasBox')?.getBoundingClientRect()
      if (!targetElem) {
        return undefined
      }

      const { x, y, width, height } = targetElem
      const { coords } = getClickCoords(
        {
          x: mouseEvent?.clientX || touchEvent?.touches[0].clientX || 0,
          y: mouseEvent?.clientY || touchEvent?.touches[0].clientY || 0,
        },
        {
          x,
          y,
          width,
          height,
        },
      )

      //* カメラからpointまで光線を伸ばす
      rayCaster.setFromCamera(coords, camera)
      rayCaster.params.Points.threshold = 0.0075

      if (!pointClouds.length) return undefined

      // if a point selection plane is set, use it to find the intersection point
      if (pointSelectionPlane) {
        const intersectPoint = new Vector3()
        const intersected = rayCaster.ray.intersectPlane(pointSelectionPlane, intersectPoint)
        if (intersected) {
          return [[intersectPoint.x, intersectPoint.y, intersectPoint.z], undefined, undefined]
        }
        return undefined
      }

      const currentEditorTool = getCurrentEditorTool(selectedTool)

      // -- decide which objects to intersect with
      const allTargetObjectIds = [...anchorPlacementObjectIds, ...pointSelectionObjectIdsRef.current]

      // find by meshRefs
      const anchorPlacementObjectsRefs = allTargetObjectIds
        .map((id) => meshRefs?.[id]?.current)
        .filter(Boolean) as Object3D[]

      // find by shape ID
      const anchorPlacementObjectsByShapeIds =
        sceneRef.current && allTargetObjectIds.length ? findObjectsOfShapeIds(sceneRef.current, allTargetObjectIds) : []

      // combine both
      const anchorPlacementObjects: Object3D[] = [...anchorPlacementObjectsRefs, ...anchorPlacementObjectsByShapeIds]

      // Lookd for solid mesh via its manager in the scene
      const solidMesh = sceneRef.current?.getObjectByName('solid-mesh-manager') || undefined

      // only alow picking point when point cloud object is visible
      const usePCD =
        ((!solidMesh && isPointCloudVisible) || (solidMesh && (isPointCloudVisible || isMeshFileVisible))) &&
        !anchorPlacementObjects.length

      // 'anchorPlacementObjects' is an exclusive setting. When it is set, only those objects will be intersected with.
      // Otherwise, the ray will intersect with the PCD if it is visible.
      // Optionally, tools can also specify that the ray should not intersect with any objects.
      const rayObjects: Object3D[] = []
      if (anchorPlacementObjects.length) {
        rayObjects.push(...anchorPlacementObjects)
      } else {
        if (usePCD) {
          if (pointClouds.length) rayObjects.push(...pointClouds)
        }

        if (solidMesh) {
          rayObjects.push(solidMesh)
        }

        if (!currentEditorTool?.config?.anchor?.notPlaceableOnObjects) {
          rayObjects.push(
            ...(Object.keys(meshRefs || {})
              .map((id) => (meshRefs && meshRefs[id].current ? meshRefs[id].current : null))
              .filter(Boolean) as Object3D[]),
          )
        }
      }

      //* 光線に衝突した点群を検出
      const intersects = rayCaster.intersectObjects(rayObjects, true)
      if (!intersects.length) {
        return undefined
      }

      // Specifically for solid mesh
      const meshIntersection = intersects
        .filter((intersect) => intersect.object.userData.isSolidMesh === true)
        .reduce<Intersection<Object3D> | undefined>((prev, current) => {
          const { distance, point } = current

          // If mask regions has been defined, filter out points that are not within them for solid mesh intersections too.
          // This ensures consistency with PCD intersection behavior.
          let result = true
          if (!isViewAllPoints && maskModelMatrices.length) {
            result = maskModelMatrices.some((mask) => {
              const localPoint = point.clone().applyMatrix4(mask.modelMatrix)
              const box = new Box3(mask.min, mask.max)
              return box.containsPoint(localPoint)
            })
          }

          if (result && (!prev || distance < prev.distance)) return current
          return prev
        }, undefined)

      // Every other intersections goes here, eg: point cloud, anchor placement objects, etc.
      const intersectedPoint = intersects
        .filter((intersect) => intersect.object.type === 'Points' || rayObjects.includes(intersect.object))
        .reduce<Intersection<Object3D> | undefined>((prev, current) => {
          const { index, faceIndex, distance, point, object } = current
          if (index === undefined && faceIndex === undefined) return prev

          // If mask regions has been defined, filter out points that are not within them but only for PCD intersections.
          // Intersections with any other object is always allowed.
          // Note that 'maskModelMatrices' has already been filtered to only include visible masks.
          let result = true
          if (object instanceof Points && !isViewAllPoints && maskModelMatrices.length) {
            result = maskModelMatrices.some((mask) => {
              const localPoint = point.clone().applyMatrix4(mask.modelMatrix)
              const box = new Box3(mask.min, mask.max)
              return box.containsPoint(localPoint)
            })
          }

          if (result && (!prev || distance < prev.distance)) return current
          return prev
        }, undefined)

      if (!intersectedPoint && !meshIntersection) {
        return undefined
      }

      const point = intersectedPoint?.point.toArray()
      const points: [PointArray | undefined, PointArray | undefined] = [
        currentEditorTool?.config?.anchor?.useSolidMesh && meshIntersection ? meshIntersection.point.toArray() : point,
        meshIntersection?.point.toArray(),
      ]

      // Deliberately not sampled here — the raycast runs on every throttled pointer move for
      // every tool, and only the chalk colour pick ever wants the texture read.
      const sampleSolidMeshColor: SolidMeshColorSampler | undefined = meshIntersection
        ? () => sampleTextureColorAtIntersection(meshIntersection)
        : undefined

      // project the selected point onto the projection plane if it is set
      if (pointProjectionPlane) {
        const projectedPoints = points.map((pt) => {
          if (!pt) return undefined

          const pointVec = new Vector3(pt[0], pt[1], pt[2])
          const projectedPoint = new Vector3()
          const projected = pointProjectionPlane.projectPoint(pointVec, projectedPoint)

          if (projected) {
            return [projectedPoint.x, projectedPoint.y, projectedPoint.z] as PointArray
          }
          return undefined
        }) as [PointArray | undefined, PointArray | undefined]

        return [...projectedPoints, sampleSolidMeshColor]
      }

      return [...points, sampleSolidMeshColor]
    },
    [
      sceneRef,
      cameraRef,
      isViewAllPoints,
      maskModelMatrices,
      pointClouds,
      selectedTool,
      anchorPlacementObjectIds,
      pointSelectionObjectIdsRef,
      meshRefs,
      isPointCloudVisible,
      isMeshFileVisible,
      pointSelectionPlane,
      pointProjectionPlane,
    ],
  )

  return { getSelectedPoint }
}

export default usePointSelection
